<template>
  <UiBackdrop :is-open="!!menuContext" :close="() => toggle(null)" />
  <component :is="isMobile ? TransitionsSlide : TransitionsFadedSlide" :direction="isMobile ? 'right' : 'bottom'">
    <SfDrawer
      ref="drawerRef"
      :model-value="!!menuContext"
      disable-click-away
      placement="top"
      class="z-30 mr-12 flex max-h-screen min-h-screen max-w-96 flex-col items-start overflow-y-auto bg-white p-0 shadow-lg md:!absolute md:!top-20 md:mr-0 md:grid md:min-h-40 md:max-w-full md:grid-cols-4 md:gap-x-6 md:p-6"
    >
      <div class="sticky top-0 flex w-full items-center justify-between bg-primary-500 px-4 py-2 md:hidden">
        <UiIconButton
          v-if="menuContext !== MenuContext.MAIN"
          id="header-menu-back"
          :icon="IconChevron"
          icon-class="rotate-90"
          class="!bg-primary-500 hover:!bg-primary-800 focus:!bg-primary-900 disabled:!bg-disabled-300"
          @click="toggle(MenuContext.MAIN)"
        />
        <div v-else />
        <Logo class="my-1.5 w-28 fill-white" />
        <UiIconButton
          id="header-menu-mobile-close"
          :icon="IconClose"
          class="!bg-primary-500 hover:!bg-primary-800 focus:!bg-primary-900 disabled:!bg-disabled-300"
          @click="toggle(null)"
          @keydown.enter.space="toggle(null)"
        />
      </div>
      <div v-for="item in menuItems" :key="item.key" class="pt-6 md:pt-0">
        <UiLink
          v-if="!item.children.length"
          :id="`menu-item-${item.key}`"
          class="flex items-center whitespace-nowrap p-4 !typography-headline-2 md:flex-col md:py-1.5"
          :to="buildUrl(item.url)"
          :underline="false"
          @click="item.action ? item.action() : toggle(null)"
        >
          <LogoSmall class="mr-6 w-12 fill-gray-100 md:mb-6 md:mr-0 md:w-1/3" />
          {{ item.label }}
        </UiLink>
        <UiLink v-else :id="`menu-item-${item.key}`" :to="buildUrl(item.url)" :underline="false" @click="toggle(null)">
          <UiHeading type="h2" role="presentation" class="whitespace-nowrap p-4 md:py-1.5">
            {{ item.label }}
          </UiHeading>
        </UiLink>
        <UiDivider v-if="item.children.length" class="mb-3.5" />
        <ul v-if="item.children.length">
          <li v-for="child in item.children" :key="child.key">
            <UiListItem
              :id="`sub-menu-item-${child.key}`"
              :tag="NuxtLinkLocale"
              :to="buildUrl(child.url, item.url)"
              class="py-4 md:py-1.5"
              @click="item.action ? item.action() : toggle(null)"
            >
              {{ child.label }}
            </UiListItem>
          </li>
        </ul>
      </div>
      <UiIconButton
        id="header-menu-close"
        :icon="IconClose"
        variant="secondary"
        class="right-2 top-2 hidden md:absolute md:block"
        @click="toggle(null)"
      />
    </SfDrawer>
  </component>
</template>

<script setup lang="ts">
import { IconChevron, IconClose, NuxtLinkLocale, TransitionsFadedSlide, TransitionsSlide } from '#components';
import LogoSmall from 'assets/images/logo-small.svg';
import Logo from 'assets/images/logo.svg';

import { type HeaderMenuProps, MenuContext } from '~/components/Header/types';
import { isAbsoluteUrl } from '~/utils/string';

const { isMobile } = useBreakpoints();

const props = defineProps<HeaderMenuProps>();

const drawerRef = ref<HTMLElement>();
const isDrawerOpen = ref<boolean>(!!props.menuContext);

const buildUrl = (childSlug?: string | null, parentSlug?: string | null): string => {
  if (!childSlug) return '';

  if (isAbsoluteUrl(childSlug)) return childSlug;

  if (parentSlug && isAbsoluteUrl(parentSlug)) return childSlug.startsWith('/') ? childSlug : `/${childSlug}`;

  const parentPath = parentSlug ? `/${parentSlug.replace(/^\/+|\/+$/g, '')}` : '';
  const childPath = childSlug.replace(/^\/+|\/+$/g, '');

  return `${parentPath}/${childPath}`;
};

watch(
  () => props.menuContext,
  () => (isDrawerOpen.value = !!props.menuContext),
);

useTrapFocus(drawerRef, {
  activeState: isDrawerOpen,
  arrowKeysUpDown: true,
  initialFocus: 'container',
});
</script>
